<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import TagMenu from './TagMenu.vue';
import ReferenceRowTags from './ReferenceRowTags.vue';

const { t } = useI18n();

/**
 * Tag row for the top of the raw view (panel and modal modes).
 *
 * Interim surface until tagging folds into a richer raw-view menu: an
 * "Add tags" textbox-styled trigger at the left that opens the shared
 * TagMenu (downward) for the single open reference, followed by the
 * reference's tags — first three as pills, the rest behind the
 * icon + count indicator with its hover tooltip (via ReferenceRowTags).
 * Session-only POC; see referenceSelectionStore.ts.
 */

const props = defineProps<{
    contentHash?: string;
}>();

const isTagMenuOpen = ref(false);
const rootEl = ref<HTMLElement | null>(null);
const triggerEl = ref<HTMLElement | null>(null);

const contentHashes = computed(() => (props.contentHash ? [props.contentHash] : []));

const openTagMenu = () => {
    isTagMenuOpen.value = true;
};

const onWindowClick = (event: MouseEvent) => {
    if (!isTagMenuOpen.value) return;
    if (rootEl.value && !rootEl.value.contains(event.target as Node)) {
        isTagMenuOpen.value = false;
    }
};

const onKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
        isTagMenuOpen.value = false;
    }
};

onMounted(() => {
    window.addEventListener('click', onWindowClick);
    window.addEventListener('keydown', onKeydown);
});

onBeforeUnmount(() => {
    window.removeEventListener('click', onWindowClick);
    window.removeEventListener('keydown', onKeydown);
});
</script>

<template>
    <div v-if="contentHash" ref="rootEl" class="raw-view-tags">
        <div ref="triggerEl" class="raw-view-tags-trigger">
            <input
                type="text"
                class="raw-view-tags-input"
                :placeholder="t('references.addTags')"
                readonly
                @click="openTagMenu"
                @focus="openTagMenu"
            />
            <TagMenu
                v-if="isTagMenuOpen"
                :content-hashes="contentHashes"
                :anchor-el="triggerEl"
                placement="down"
                @close="isTagMenuOpen = false"
            />
        </div>
        <ReferenceRowTags :content-hash="contentHash" :max-pills="4" overflow-append />
    </div>
</template>
